import React, { useCallback, useEffect, useState } from 'react';
import { Virtual } from 'swiper/modules';
// Swiper base CSS comes from the same modules the source surface
// uses; importing here keeps the swiper-overlay / swiper-container
// global utility classes loaded inside the new ActionWindows tree.
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';

import { OVERLAYS_URL } from '../../../Settings';
import blankImage from '../../TwoD/Common/blank-512x512.png';
import { getTextureUrl } from '../../TwoD/Common/TextureTools';

import '../../TwoD/Components/Controls/Actions/action-scroller.css';
import './ResearchInventory.css';

// ResearchInventory — verbatim port of InventorySelector. Always
// rendered as the baseline row of the Research window. Listens for
// `highlight-storage-event` / `clear-storage-highlight-event` on
// `window`. When a highlighted slot is clicked, fires the payload's
// callback with swiper.clickedIndex and clears local highlight state.
//
// Source preserved verbatim: storage is rendered as `[...storage,
// null]` (the trailing blank slot), the swiper container carries the
// `research-inventory-animated-border-dashed` class when the first
// highlighted index points at an empty slot (border-pulse), and the
// onClick gate is `highlightedIndexes.includes(swiper.clickedIndex)`.
export function ResearchInventory({ gamePlayerData }) {
    const [highlight, setHighlight] = useState(false);
    const [highlightedIndexes, setHighlightedIndexes] = useState([]);
    const [storageCallback, setStorageCallback] = useState(null);

    const clearHighlights = useCallback(() => {
        setHighlight(false);
        setHighlightedIndexes([]);
        setStorageCallback(null);
    }, []);

    useEffect(() => {
        const highlightEvent = (e) => {
            setHighlight(true);
            setHighlightedIndexes(e.detail.indexes || []);
            // Store the raw function. useState's functional-set
            // overload means we have to wrap it.
            setStorageCallback(() => e.detail.callback);
        };
        window.addEventListener(
            'highlight-storage-event', highlightEvent);
        window.addEventListener(
            'clear-storage-highlight-event', clearHighlights);
        return () => {
            window.removeEventListener(
                'highlight-storage-event', highlightEvent);
            window.removeEventListener(
                'clear-storage-highlight-event', clearHighlights);
        };
    }, [clearHighlights]);

    const onClick = (swiper) => {
        const isValid = highlightedIndexes.includes(swiper.clickedIndex);
        if (isValid && storageCallback) {
            storageCallback(swiper.clickedIndex);
            clearHighlights();
        }
    };

    const baseStorage = (gamePlayerData
        && Array.isArray(gamePlayerData.storage))
        ? gamePlayerData.storage : [];
    const storage = [...baseStorage, null];

    // Border-pulse animation: source line 90-96 — fires when the
    // first highlighted index in storage holds `undefined`, signalling
    // the player needs to make space first.
    const firstHighlightIndex = highlightedIndexes[0];
    const firstHighlighted = (gamePlayerData
        && Array.isArray(gamePlayerData.storage))
        ? gamePlayerData.storage[firstHighlightIndex]
        : undefined;
    const highlightStorageArea = firstHighlighted === undefined;

    let className = 'research-inventory-swiper-container';
    if (highlight && highlightStorageArea) {
        className += ' research-inventory-animated-border-dashed';
    }

    const roomImage = (props) => {
        const src = props.texture
            ? getTextureUrl(props.texture)
            : blankImage;
        return (
            <div data-testid={'research-inventory-slot'}
                 data-slot-index={props.index}
                 data-tname={props.texture || undefined}>
                <img src={src}
                     alt={'room'}
                     className={'research-inventory-image'}/>
                {highlightedIndexes.includes(props.index) && (
                    <div className={'research-inventory-highlighter'}
                         data-testid={
                             'research-inventory-highlight'}/>
                )}
            </div>
        );
    };

    return (
        <div className={'action-scroller'}
             data-testid={'research-inventory'}>
            <div className={'research-inventory-anchor'}>
                <div className={className}>
                    <Swiper slidesPerView={6}
                            spaceBetween={0}
                            freeMode={true}
                            grabCursor={true}
                            modules={[Virtual]}
                            onClick={onClick}
                            className={'action-scroller-swiper'}>
                        {storage.map((gridItem, index) => (
                            <SwiperSlide key={'gi-' + index}
                                         virtualIndex={index}>
                                {roomImage({
                                    texture: gridItem,
                                    index: index,
                                })}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                <img src={OVERLAYS_URL
                        + 'InventoryOverlay-1080x210.png'}
                     alt={'action'}
                     data-testid={'research-inventory-overlay'}
                     className={'swiper-overlay'}/>
            </div>
        </div>
    );
}

export default ResearchInventory;
